import StartPageControl from '../../components/common/startPageControl';
import { readSidebarExpanded, saveSidebarExpanded } from '../../util/sidebarPreference';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { timelyIconStyle } from '../../components/headers/timelyIconStyle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import React, { useState, useEffect } from "react";
import { styled, useTheme, Theme, CSSObject, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQuery } from "@apollo/client";
import { CalendarNavIcon, BookingTypesNavIcon } from '../../components/headers/navigationIcons';
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ThemeToggleButton from "../../assets/css/themeToggleButton";
import InstallDesktopRoundedIcon from '@mui/icons-material/InstallDesktopRounded';
import InstallAppDialog from '../../../apps/timely/InstallAppDialog';
import { INSTALL_CHANGED, isAppInstalled } from '../../../apps/timely/installPrompt';
import { useIsMobile } from "../../util/utils";
import { useSelectedBusiness } from "../../redux/store";
import { setSelectedBusiness } from "../../redux/actions/businessActions";

import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { UserContext } from "../../components/auth/auth-context";
import { Permissions, hasPermission } from "../../util/accessControl";
import { GET_BUSINESS_CATEGORIES } from "../../graphql/queries";
import {
  hasCareWorkspaceAccess,
  hasStandaloneBookingAccess,
  isProductSubscribed,
  isProductVisible,
} from "../../../core/business/productAccess";
import {
  isCareCategoryName,
} from "../../../core/business/category";
import {
  detectVerticalFromPath,
  isVerticalId,
} from "../../verticals/registry";
import { getStoredVertical } from "../../routes/verticalPref";
import { getBrandVertical, getNormalizedHostname, isLocalHostname } from "../../util/brandDomains";
import { getRuntimeAppProfile } from "../../runtime/appProfile";


const LOCAL_WORKSPACE_PRODUCTS_KEY = "planyLocalWorkspaceProducts";
const WORKSPACE_PRODUCTS_CHANGED_EVENT = "plany:workspace-products-change";

function readLocalWorkspaceProducts(businessId: number | null) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_WORKSPACE_PRODUCTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const guestProducts = parsed.guest || {};
    const businessProducts = businessId ? parsed[String(businessId)] || {} : {};
    return { ...guestProducts, ...businessProducts };
  } catch {
    return {};
  }
}

function hasLocalWorkspaceOverride(
  localWorkspaceProducts: Record<string, boolean>,
  productId: string
) {
  return Object.prototype.hasOwnProperty.call(localWorkspaceProducts, productId);
}

function resolveWorkspaceProductState(
  providerUser: any,
  localWorkspaceProducts: Record<string, boolean>,
  productId: string,
  defaultWorkspaceAccess = false
) {
  if (hasLocalWorkspaceOverride(localWorkspaceProducts, productId)) {
    return Boolean(localWorkspaceProducts[productId]);
  }

  if (isProductSubscribed(providerUser, productId)) {
    return true;
  }

  return Boolean(defaultWorkspaceAccess);
}

const closedDrawerWidth = 72;
const expandedDrawerWidth = 256;
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `${closedDrawerWidth}px`,
  [theme.breakpoints.up("sm")]: {
    width: `${closedDrawerWidth}px`,
  },
});
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(0.5, 1),
  minHeight: 56,
}));
const Drawer = styled(MuiDrawer)(({ theme }) => ({
  width: closedDrawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...closedMixin(theme),
  "& .MuiDrawer-paper": {
    ...closedMixin(theme),
    borderRight: "1px solid #252b35",
    background: "#252b35",
    boxShadow: "none",
    color: "#b6c0ce",
  },
}));
const Content = styled("main")(({ theme }) => ({
  flex: "1 1 0%",
  width: 0,
  padding: theme.spacing(0),
  marginTop: 0,
  minWidth: 0,
  overflowX: "auto",
  minHeight: "100vh",
  background:
    theme.customSurfaces?.pageBackgroundGradient || theme.palette.background.default,
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
}));

interface PortalProps {
  TabComponent: React.ComponentType<any>;
  title: string;
  props: any;
}
const Portal: React.FC<PortalProps> = ({ TabComponent, props }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { providerUser } = React.useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const businessId =
    Number(selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId || 0) || null;
  const [localWorkspaceProducts, setLocalWorkspaceProducts] = useState(() =>
    readLocalWorkspaceProducts(businessId)
  );
  useEffect(() => {
    setLocalWorkspaceProducts(readLocalWorkspaceProducts(businessId));
  }, [businessId, location.search]);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleWorkspaceProductsChanged = (event: any) => {
      const detail = event?.detail;
      if (!businessId || Number(detail?.businessId) !== businessId) return;
      setLocalWorkspaceProducts(
        detail?.products && typeof detail.products === "object"
          ? detail.products
          : {}
      );
    };

    window.addEventListener(
      WORKSPACE_PRODUCTS_CHANGED_EVENT,
      handleWorkspaceProductsChanged
    );
    return () => {
      window.removeEventListener(
        WORKSPACE_PRODUCTS_CHANGED_EVENT,
        handleWorkspaceProductsChanged
      );
    };
  }, [businessId]);
  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
  const businessStaffs = React.useMemo(
    () => selectedBusiness?.businessStaffs || [],
    [selectedBusiness?.businessStaffs]
  );
  const isAFHName = React.useCallback(
    (name?: string) => isCareCategoryName(name),
    []
  );
  const selectedCategory = React.useMemo(
    () =>
      selectedBusiness?.businessSubcategory ||
      (categoriesData?.getBusinessCategories || []).find(
        (c: any) =>
          Number(c?.id) ===
          Number(
            selectedBusiness?.businessBasic?.businessSubcategoryId ||
              selectedBusiness?.businessBasic?.businessCategoryId
          )
      ) ||
      null,
    [
      categoriesData?.getBusinessCategories,
      selectedBusiness?.businessBasic?.businessCategoryId,
      selectedBusiness?.businessBasic?.businessSubcategoryId,
      selectedBusiness?.businessSubcategory,
    ]
  );
  const isAFHBusiness = React.useMemo(
    () =>
      isAFHName(selectedCategory?.name) ||
      Boolean(selectedBusiness?.careSettings?.afhLicenseNumber) ||
      Boolean(selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber),
    [
      selectedCategory?.name,
      isAFHName,
      selectedBusiness?.careSettings?.afhLicenseNumber,
      selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber,
    ]
  );
  const isCareEligible = React.useMemo(
    () => isAFHBusiness,
    [isAFHBusiness]
  );
  const canUseStandaloneBooking = React.useMemo(
    () => hasStandaloneBookingAccess(providerUser, isAFHBusiness),
    [providerUser, isAFHBusiness]
  );
  const hasBookingSubscription = React.useMemo(
    () =>
      resolveWorkspaceProductState(
        providerUser,
        localWorkspaceProducts,
        "booking",
        canUseStandaloneBooking
      ),
    [canUseStandaloneBooking, localWorkspaceProducts, providerUser]
  );
  const canUseCareWorkspace = false;
  const activeProductId = React.useMemo(() => {
    if (!isLocalHostname(getNormalizedHostname())) {
      return getRuntimeAppProfile().verticalId;
    }
    const detectedFromPath = detectVerticalFromPath(location.pathname);
    if (detectedFromPath) return detectedFromPath;

    const brandVertical = getBrandVertical();
    if (brandVertical) return brandVertical;

    const params = new URLSearchParams(location.search);
    const verticalFromQuery = params.get("v");
    if (verticalFromQuery && isVerticalId(verticalFromQuery)) {
      return verticalFromQuery;
    }

    const storedVertical = getStoredVertical();
    if (storedVertical && isVerticalId(storedVertical)) {
      return storedVertical;
    }


    return isAFHBusiness ? "care" : "booking";
  }, [canUseCareWorkspace, isAFHBusiness, location.pathname, location.search]);
  const canUseBookingPortal = React.useMemo(
    () =>
      canUseStandaloneBooking ||
      hasBookingSubscription ||
      isProductVisible(providerUser, "booking"),
    [canUseStandaloneBooking, hasBookingSubscription, providerUser]
  );
  const portalProductId =
    "booking";
  const isCarePortal = false;
  const isBookingPortal = portalProductId === "booking";
  const withProduct = React.useCallback(
    (path: string, explicitProductId?: "care" | "booking") =>
      `${path}?v=${explicitProductId || portalProductId}`,
    [portalProductId]
  );
  const homeRoute = withProduct("/biz/bookingtypes", "booking");
  const currentLoginStaff = React.useMemo(() => {
    const byStaffId = businessStaffs.find(
      (staff: any) => Number(staff?.id) === Number(providerUser?.staffId)
    );
    if (byStaffId) return byStaffId;
    const providerUserId = Number(providerUser?.userId || 0);
    if (!providerUserId) return null;
    return (
      businessStaffs.find(
        (staff: any) => Number(staff?.user?.id) === providerUserId
      ) || null
    );
  }, [businessStaffs, providerUser?.staffId, providerUser?.userId]);
  const rawPermissions = currentLoginStaff?.role?.permissions;
  const userPermissions = React.useMemo(() => {
    if (Array.isArray(rawPermissions)) return rawPermissions.filter(Boolean).map(String);
    if (typeof rawPermissions === "string") {
      try {
        const parsed = JSON.parse(rawPermissions);
        return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
      } catch (_err) {
        return [];
      }
    }
    return [];
  }, [rawPermissions]);
  const can = React.useCallback(
    (permission: string) =>
      hasPermission(
        userPermissions,
        permission,
        currentLoginStaff?.id ?? null,
        currentLoginStaff?.id ?? null
      ),
    [userPermissions, currentLoginStaff?.id]
  );
  const isBusinessOwner =
    Number(providerUser?.userId || 0) > 0 &&
    Number(selectedBusiness?.businessBasic?.createdBy || 0) ===
    Number(providerUser?.userId || 0);
  const canViewCalendar = isBusinessOwner || can(Permissions.Calendar.VIEW);
  const canViewBookingTypes =
    isBusinessOwner || can(Permissions.BookingType.VIEW);
  const canViewResidents =
    false;
  const canViewStaff = isBusinessOwner || can(Permissions.Staff.VIEW);
  const canViewJobs =
    canViewBookingTypes ||
    can(Permissions.Job.VIEW) ||
    can(Permissions.Job.ADD) ||
    can(Permissions.Job.EDIT);
  const hasBusiness = Boolean(Number(selectedBusiness?.businessBasic?.id || 0));
  const canViewHiringMarketplace =
    false;
  const bookingNavLabel = "Booking Types";
  const bookingNavTooltip = "Booking types";
  const calendarNavTooltip = "Booking calendar";
  const subscriptionsPath = `/biz/pricing?product=${portalProductId}&v=${portalProductId}`;
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isActiveRoute = (basePath: string) =>
    location.pathname === basePath ||
    location.pathname.startsWith(`${basePath}/`);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [appInstalled, setAppInstalled] = useState(isAppInstalled);
  useEffect(() => {
    const display = window.matchMedia('(display-mode: standalone)');
    const update = () => {
      const installed = isAppInstalled();
      setAppInstalled(installed);
      if (installed) setInstallOpen(false);
    };
    update();
    window.addEventListener(INSTALL_CHANGED, update);
    display.addEventListener('change', update);
    return () => {
      window.removeEventListener(INSTALL_CHANGED, update);
      display.removeEventListener('change', update);
    };
  }, []);
  const careOperationsSectionActive =
    isActiveRoute("/care/schedule") ||
    isActiveRoute("/biz/resident") ||
    isActiveRoute("/biz/staff");
  const isHiringActive =
    isActiveRoute("/biz/hiring") ||
    isActiveRoute("/biz/manage/jobs") ||
    /^\/b\/[^/]+\/manage\/jobs(\/|$)/.test(location.pathname);
  const isMarketplaceActive = isActiveRoute("/biz/marketplace");
  const growthSectionActive = isHiringActive || isMarketplaceActive;
  const settingsSectionActive =
    isActiveRoute("/biz/business") ||
    isActiveRoute("/biz/payments") ||
    isActiveRoute("/biz/pricing") ||
    isActiveRoute("/security");
  const [careOperationsOpen, setCareOperationsOpen] = useState(
    careOperationsSectionActive
  );
  const [growthOpen, setGrowthOpen] = useState(growthSectionActive);
  const [settingsOpen, setSettingsOpen] = useState(settingsSectionActive);
  const [desktopExpanded, setDesktopExpanded] = useState(() => readSidebarExpanded(providerUser?.userId));
  useEffect(() => {
    setDesktopExpanded(readSidebarExpanded(providerUser?.userId));
  }, [providerUser?.userId]);
  const toggleDesktopMenu = () => {
    const expanded = !desktopExpanded;
    setDesktopExpanded(expanded);
    saveSidebarExpanded(providerUser?.userId, expanded);
  };
  const collapsedDesktop = !isMobile && !desktopExpanded;


  useEffect(() => {
    if (careOperationsSectionActive) setCareOperationsOpen(true);
  }, [careOperationsSectionActive]);

  useEffect(() => {
    if (growthSectionActive) setGrowthOpen(true);
  }, [growthSectionActive]);

  useEffect(() => {
    if (settingsSectionActive) setSettingsOpen(true);
  }, [settingsSectionActive]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const rootStyle = document.documentElement.style;
    rootStyle.setProperty("--header-brand-icon-display", "none");

    if (isMobile) {
      rootStyle.setProperty(
        "--portal-drawer-offset",
        "calc(64px + 1px)"
      );
      rootStyle.setProperty("--portal-mobile-toggle-space", "84px");
    } else {
      const collapsedOffset = `${desktopExpanded ? expandedDrawerWidth : closedDrawerWidth}px`;
      rootStyle.setProperty(
        "--portal-drawer-offset",
        collapsedOffset
      );
      rootStyle.setProperty("--portal-mobile-toggle-space", "0px");
    }

    return () => {
      rootStyle.removeProperty("--header-brand-icon-display");
      rootStyle.removeProperty("--portal-drawer-offset");
      rootStyle.removeProperty("--portal-mobile-toggle-space");
    };
  }, [isMobile, desktopExpanded]);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const isPricing = location.pathname === "/biz/pricing";
    const isBillingFlow = location.pathname.startsWith("/billing/");
    if (!isPricing && !isBillingFlow) {
      try {
        localStorage.setItem("lastBizRouteBeforePricing", currentPath);
      } catch (_error) {
        // ignore storage failures
      }
    }
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const raw = localStorage.getItem("pendingUpgradeNavigation");
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const path = typeof parsed?.path === "string" ? parsed.path : "";
      const targetBusinessId = Number(parsed?.businessId || 0);
      const createdAt = Number(parsed?.createdAt || 0);
      const isFresh = createdAt > 0 && Date.now() - createdAt < 2 * 60 * 60 * 1000;

      if (!path || !isFresh) {
        localStorage.removeItem("pendingUpgradeNavigation");
        return;
      }
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      if (currentPath === path) {
        localStorage.removeItem("pendingUpgradeNavigation");
        return;
      }

      if (
        targetBusinessId > 0 &&
        Number(selectedBusiness?.businessBasic?.id || 0) !== targetBusinessId
      ) {
        dispatch(setSelectedBusiness(targetBusinessId));
      }

      localStorage.removeItem("pendingUpgradeNavigation");
      navigate(path, { replace: true });
    } catch (_error) {
      localStorage.removeItem("pendingUpgradeNavigation");
    }
  }, [location.pathname, location.search, location.hash, navigate, dispatch, selectedBusiness]);

  const handleMobileToggle = () => setMobileOpen((o) => !o);
  const handleNavigation = (
    path: string,
    event: React.MouseEvent,
    mobile = false
  ) => {
    navigate(path, { state: { from: window.location.href } });
    if (mobile) setMobileOpen(false);
  };
  const listItemTextStyle = {
    ...(collapsedDesktop
      ? {
        mt: 0.55,
        mb: 0,
        textAlign: "center",
        "& .MuiTypography-root": {
          fontSize: "0.7rem",
          lineHeight: 1.2,
          whiteSpace: "normal",
          wordBreak: "normal",
          letterSpacing: "0.015em",
        },
      }
      : {
        "& .MuiTypography-root": {
          fontSize: "0.9rem",
          letterSpacing: "0.01em",
        },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }),
  };

  const listLayoutSx = {
    flexShrink: 0,
    "& .MuiListItemText-root": {
      display: collapsedDesktop ? "none" : "block",
    },
    "& .MuiListItemButton-root, & .MuiListItem-root.MuiButtonBase-root": {
      width: "auto",
      minWidth: 0,
      mx: collapsedDesktop ? 1 : 1.25,
      my: 0.4,
      px: collapsedDesktop ? 1 : 1.5,
      py: collapsedDesktop ? 1.25 : 0.95,
      borderRadius: 2,
      minHeight: 44,
      transition: "all 180ms ease",
      color: "#b6c0ce",
      "&:hover": {
        bgcolor: "#303846",
        color: "#ffffff",

      },
    },
    "& .MuiListItem-root": collapsedDesktop
      ? {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }
      : {},
    "& .MuiListItemIcon-root": {
      minWidth: collapsedDesktop ? 0 : 36,
      justifyContent: "center",
      mb: 0,
      color: "inherit",
      opacity: 0.95,
      "& .MuiSvgIcon-root": {
        fontSize: collapsedDesktop ? 22 : 21,
      },
    },
    "& .MuiTypography-root": {
      color: "inherit",
      fontWeight: 500,
    },
  };

  const submenuListSx = {
    mx: collapsedDesktop ? 0.5 : 2,
    mb: 0.75,
    py: 0.5,
    pl: collapsedDesktop ? 0 : 1,
    borderLeft: collapsedDesktop ? "none" : `2px solid ${theme.palette.divider}`,
    borderRadius: 2,
    bgcolor: "transparent",
  };

  const submenuItemSx = {
    mx: "0 !important",
    my: "2px !important",
    px: collapsedDesktop ? "6px !important" : "10px !important",
    py: "7px !important",
    minHeight: `${collapsedDesktop ? 48 : 42}px !important`,
    borderRadius: "8px !important",
    alignItems: "center",
    ...(collapsedDesktop
      ? {
        flexDirection: "column",
        gap: 0.25,
      }
      : {
        flexDirection: "row",
      }),
    "& .MuiListItemIcon-root": {
      minWidth: collapsedDesktop ? 0 : 28,
      mb: 0,
      opacity: 0.8,
      "& .MuiSvgIcon-root": {
        fontSize: 17,
      },
    },
  };

  const submenuTextStyle = {
    width: "100%",
    minWidth: 0,
    m: 0,
    textAlign: collapsedDesktop ? "center" : "left",
    "& .MuiTypography-root": {
      display: "block",
      width: "100%",
      fontSize: collapsedDesktop ? "0.64rem" : "0.82rem",
      lineHeight: 1.15,
      whiteSpace: "normal",
      overflowWrap: "break-word",
      wordBreak: "normal",
      letterSpacing: "0.01em",
    },
  };

  const mainListItems = (
    <List
      sx={{
        ...listLayoutSx,
        "& .Mui-selected": {
          bgcolor: "#354153",
          boxShadow: "none",
        },
        "& .Mui-selected .MuiListItemIcon-root": {
          color: "#ffffff",
        },
        "& .Mui-selected .MuiTypography-root": {
          color: "#ffffff",
          fontWeight: 700,
        },
      }}
    >
      {/* AFH Dashboard */}

      {/* Timely365 operations */}

      {/* Booking types */}
      {canUseBookingPortal && (
        <Tooltip
          title={hasBusiness ? bookingNavTooltip : "Choose or create a workspace"}
          placement="right"
        >
          <span>
            <ListItem
              button
              selected={isActiveRoute("/biz/bookingtypes")}
              disabled={!hasBusiness || !canViewBookingTypes}
              onClick={(e) => handleNavigation(withProduct("/biz/bookingtypes", "booking"), e)}
            >
              <ListItemIcon><BookingTypesNavIcon /></ListItemIcon>
              <ListItemText primary={bookingNavLabel} sx={listItemTextStyle} />
            </ListItem>
          </span>
        </Tooltip>
      )}
      {/* Calendar */}
      {canUseBookingPortal && (
        <Tooltip
          title={hasBusiness ? calendarNavTooltip : "Choose or create a workspace"}
          placement="right"
        >
          <span>
            <ListItem
              button
              selected={isActiveRoute("/biz/calendar")}
              disabled={!hasBusiness || !canViewCalendar}
              onClick={(e) => handleNavigation(withProduct("/biz/calendar", "booking"), e)}
            >
              <ListItemIcon>
                <CalendarNavIcon />
              </ListItemIcon>
              <ListItemText primary="Calendar" sx={listItemTextStyle} />
            </ListItem>
          </span>
        </Tooltip>
      )}
      {/* Timely365 team */}
      {<Tooltip
          title={hasBusiness ? (selectedBusiness?.businessBasic?.workspaceType === "PERSONAL" ? "My profile, availability and calendar connections" : "Team members, availability and calendar connections") : "Choose or create a workspace"}
          placement="right"
        >
          <span>
            <ListItem
              button
              selected={isActiveRoute("/biz/staff")}
              disabled={!hasBusiness || !canViewStaff}
              onClick={(e) => handleNavigation(withProduct("/biz/staff"), e)}
            >
              <ListItemIcon>{selectedBusiness?.businessBasic?.workspaceType === "PERSONAL" ? <PersonOutlineRoundedIcon /> : <PeopleIcon />}</ListItemIcon>
              <ListItemText primary={selectedBusiness?.businessBasic?.workspaceType === "PERSONAL" ? "My profile" : "Team"} sx={listItemTextStyle} />
            </ListItem>
          </span>
        </Tooltip>}
    </List>
  );

  const bottomListItems = (
    <List
      sx={{
        ...listLayoutSx,
        mt: "auto",
        "& .Mui-selected": {
          bgcolor: "#354153",
          boxShadow: "none",
        },
        "& .Mui-selected .MuiListItemIcon-root": {
          color: "#ffffff",
        },
        "& .Mui-selected .MuiTypography-root": {
          color: "#ffffff",
          fontWeight: 700,
        },
      }}
    >
      {getRuntimeAppProfile().id === 'timely' && !appInstalled && (
        <Tooltip title="Install Timely365 or add a shortcut" placement="right">
          <ListItem button onClick={() => setInstallOpen(true)}>
            <ListItemIcon><InstallDesktopRoundedIcon /></ListItemIcon>
            <ListItemText primary="App & shortcuts" sx={listItemTextStyle} />
          </ListItem>
        </Tooltip>
      )}
      <Divider />

      <Divider />
      {<>
          <Tooltip title="Settings" placement="right">
            <ListItem
              button
              selected={settingsSectionActive}
              aria-expanded={settingsOpen}
              onClick={() => setSettingsOpen((open) => !open)}
            >
              <ListItemIcon><SettingsRoundedIcon /></ListItemIcon>
              <ListItemText primary="Settings" sx={listItemTextStyle} />
              {!collapsedDesktop &&
                (settingsOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />)}
            </ListItem>
          </Tooltip>
          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={submenuListSx}>
              <ListItem
                button
                selected={isActiveRoute("/biz/business")}
                onClick={(e) => handleNavigation(withProduct("/biz/business"), e, isMobile)}
                sx={submenuItemSx}
              >
                <Tooltip title={selectedBusiness?.businessBasic?.workspaceType === "PERSONAL" ? "Personal profile" : "Business profile"} placement="right">
                  <ListItemIcon><BusinessIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                </Tooltip>
                <ListItemText primary={selectedBusiness?.businessBasic?.workspaceType === "PERSONAL" ? "Personal profile" : "Business profile"} sx={submenuTextStyle} />
              </ListItem>
              <ListItem
                button
                selected={isActiveRoute("/biz/pricing")}
                onClick={(e) => handleNavigation(subscriptionsPath, e, isMobile)}
                sx={submenuItemSx}
              >
                <Tooltip title="Subscription & Billing" placement="right">
                  <ListItemIcon><UpgradeIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Billing" sx={submenuTextStyle} />
              </ListItem>
              <ListItem
                button
                selected={isActiveRoute("/security")}
                onClick={(e) => handleNavigation("/security", e, isMobile)}
                sx={submenuItemSx}
              >
                <Tooltip title="Security and Privacy" placement="right">
                  <ListItemIcon><SecurityIcon sx={{ fontSize: 19 }} /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Security" sx={submenuTextStyle} />
              </ListItem>
              <StartPageControl sx={submenuItemSx} textSx={submenuTextStyle} />
              <ThemeToggleButton sx={submenuItemSx} textSx={submenuTextStyle} />
            </List>
          </Collapse>
        </>}
    </List>
  );

  const drawer = (
    <>
      <DrawerHeader>
        <Tooltip title={isMobile || desktopExpanded ? "Collapse menu" : "Expand menu"} placement="right">
        <IconButton
          aria-label={isMobile || desktopExpanded ? "Collapse menu" : "Expand menu"}
          aria-expanded={isMobile ? mobileOpen : desktopExpanded}
          aria-controls="timely-business-navigation"
          onClick={() => isMobile ? setMobileOpen(false) : toggleDesktopMenu()}
          sx={{
            width: 56,
            height: 48,
            flexShrink: 0,
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            textDecoration: "none",
            borderRadius: 2,
            position: "relative",
            overflow: "visible",
            "&:focus-visible": { outline: "2px solid #93c5fd", outlineOffset: 2 },
          }}
        >
          <span
            aria-hidden="true"
            style={{
              ...timelyIconStyle,
              width: 40,
              height: 40,
              flexShrink: 0,
              objectFit: "contain",
              transform: "scale(1.8)",
              display: "block",
            }}
          />
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              width: 16,
              height: 18,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#788596",
              pointerEvents: "none",
            }}
          >
            {isMobile || desktopExpanded
              ? <ChevronLeftIcon sx={{ fontSize: 16 }} />
              : <ChevronRightIcon sx={{ fontSize: 16 }} />}
          </Box>
        </IconButton>
        </Tooltip>
      </DrawerHeader>
      <Box
        id="timely-business-navigation"
        component="nav"
        aria-label="Business navigation"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          pt: 0.75,
          pb: 1.25,
        }}
      >
        {mainListItems}
        {bottomListItems}
      </Box>
    </>
  );

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        {isMobile && !mobileOpen && (
          <IconButton
            color="inherit"
            aria-label="Expand menu"
            aria-expanded={false}
            aria-controls="timely-business-navigation"
            onClick={handleMobileToggle}
            sx={{
              position: "fixed",
              top:
                location.pathname === "/profile"
                  ? theme.spacing(2.5)
                  : theme.spacing(1),
              left: theme.spacing(1),
              zIndex: theme.zIndex.drawer + 2,
              width: 76,
              minHeight: 44,
              gap: 1,
              borderRadius: 2,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,

              boxShadow: "none",
              "&:hover": {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <Box component="span" aria-hidden="true" sx={{ ...timelyIconStyle, display: "block", width: 28, height: 28, transform: "scale(1.8)" }} />
            <MenuRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: 'text.secondary' }} />
          </IconButton>
        )}
        {!isMobile ? (
          <Drawer variant="permanent" sx={{
            width: { sm: desktopExpanded ? expandedDrawerWidth : closedDrawerWidth },
            "& .MuiDrawer-paper": { width: { sm: desktopExpanded ? expandedDrawerWidth : closedDrawerWidth } },
          }}>
            {drawer}
          </Drawer>
        ) : (
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleMobileToggle}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: 288,
                background: "#252b35",
                color: "#b6c0ce",
              },
            }}
          >
            {drawer}
          </MuiDrawer>
        )}
        <Content>
          <TabComponent {...props} />
        </Content>
        {getRuntimeAppProfile().id === 'timely' && <InstallAppDialog open={installOpen} onClose={() => setInstallOpen(false)} />}
      </Box>
    </>
  );
};

export default Portal;
